import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MainButton } from "../../components/MainButton";
import { Menu } from "../../components/Menu";
import { SearchOrderItem } from "../../components/SearchOrderItem";
import InputMask from "react-input-mask";

import { ConfigType } from "../../contexts/AppConfigContext";
import { Loading } from "../../components/Loading";
import { useAppConfig } from "../../hooks/useAppConfig";
import "./index.css";
import api from "../../services/api";
import { usePluginspace } from "../../hooks/usePluginspace";
import { cpfCnpjNoMask, datetimeToDateBR, cpfMask } from "../../functions/mask";
import { centsToFloat } from "../../functions/price_functions";
import Swal from "sweetalert2";
import { ItemFractionalType } from "../../contexts/OrderContext";
import { AdditionalType } from "../../contexts/OrderContext";

type UserOrderType = {
  name: string;
  cpf: string;
};

type ProductOrderType = {
  id: number;
  sku?: string;
  name: string;
  quantity: number;
  unitPrice: number;
  promotionalPrice: number;
  variation: string;
  fractions: ItemFractionalType[];
  additionals: AdditionalType[];
  observation?: string;
};

export type OrderFindedType = {
  id: number;
  ext: string;
  date: string;
  observation: string;
  type: string;
  status: Array<StatusResponseType>;
  allStatus: Array<StatusResponseType>;
  store: string;
  address: string;
  user: UserOrderType;
  products: ProductOrderType[];
  totalValue: number;
  freight: number;
  statusId: number;
  content: any;
  locationName?: string;
  cartCustomer?: any;
  externalCodeERP?: any;
};

type StatusResponseType = {
  id: number;
  name: string;
  slug: string;
  order: number;
  displayStatusWebapp: boolean;
  colorSetting?: {};
};

type ProductResponseType = {
  id: number;
  code: string;
  content: any;
  sku: string;
  price: number;
  quantity: number;
  discount: number;
};

type PluginspaceResponseType = {
  name: string;
};

type OrderResponseType = {
  id: number;
  classifierId: number | null;
  content: any;
  statusId: number;
  created: string;
  customerId: string;
  deliveryTax: number;
  discountGeral: number;
  locationId: number;
  orderidExt: string;
  pluginconfigId: number;
  pluginspaceId: number;
  products: Array<ProductResponseType>;
  status: Array<StatusResponseType>;
  type: string;
  pluginspace: PluginspaceResponseType;
  observation: string;
  cartCustomer?: any;
  location?: {
    name: string;
  };
};

export function SearchOrders() {
  const navigate = useNavigate();
  const { getToken, registerAccess } = usePluginspace();

  const [cpfOrCnpj, setCpfOrCnpj] = useState("");
  const [numberOrder, setNumberOrder] = useState("");
  const [findedOrders, setFindedOrders] = useState<Array<OrderFindedType>>([]);
  const [maskCpfjCnpj, setMaskCpfjCnpj] = useState("999.999.999-99");
  const { getAppConfig } = useAppConfig();
  const [config, setConfig] = useState<ConfigType | void>();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<Array<StatusResponseType>>([]);
  const [disableNumber, setDisableNumber] = useState(false);
  const [disableDoc, setDisableDoc] = useState(false);
  const [loadingHeader, setLoadingHeader] = useState(true);
  const [page, setPage] = useState(0);
  const [searchMore, setSearchMore] = useState(false);
  const [loaderLocation, setLoaderLocation] = useState(false);

  useEffect(() => {
    registerAccess("search-order");
    getConfig();
    getStatus();
  }, []);

  async function getConfig() {
    setLoadingHeader(true);
    setConfig(await getAppConfig());
    setLoadingHeader(false);
    setLoading(false);
  }

  async function getStatus() {
    await api
      .get(`/api/status`, {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then((response) => {
        const statusReponse = response.data;
        const sts = statusReponse.data.filter(
          (manyStatus: StatusResponseType) =>
            manyStatus.displayStatusWebapp === true
        );
        setStatus(
          sts.sort((a: StatusResponseType, b: StatusResponseType) =>
            a.order > b.order ? 1 : b.order > a.order ? -1 : 0
          )
        );
      })
      .catch((err) => {
        console.log(err);
        return null;
      });
  }

  async function searchMoreOrders() {
    try {
      setLoaderLocation(true);
      const skip = page + 2;
      await api
        .post(
          `/api/orders/filter?take=10&skip=${skip}&page=${page}&order=DESC`,
          {
            cpf: cpfCnpjNoMask(cpfOrCnpj),
          },
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then((response) => {
          const orderResponse = response.data;

          const newOrders = orderResponse.data.map(
            (order: OrderResponseType) => {
              let orderAddress = null;
              if (order.content && order.content.orderAddress) {
                orderAddress = order.content.orderAddress;
              }
              let totalValue = 0;
              order.products.map((product) => {
                totalValue += product.price * product.quantity;
                return;
              });
              return {
                id: order.id,
                ext: order.orderidExt,
                cartCustomer: order.cartCustomer,
                date: datetimeToDateBR(order.created),
                type: order.type,
                statusId: order.statusId,
                status: order.status.sort((a, b) => a.order - b.order),
                allStatus: status,
                store: order.pluginspace.name,
                observation: order.observation,
                address: orderAddress
                  ? `${orderAddress.address} - ${orderAddress.district} - ${
                      orderAddress.city
                    } - ${orderAddress.cep ? orderAddress.cep : ""} ${
                      orderAddress.complement
                        ? " - " + orderAddress.complement
                        : ""
                    }`
                  : "",
                user: {
                  name:
                    order.content && order.content.customer
                      ? order.content.customer
                      : "",
                  cpf:
                    order.content && order.content.customerCpf
                      ? cpfMask(order.content.customerCpf)
                      : "",
                },
                products: order.products.map((product) => ({
                  id: product.id,
                  sku: product.sku,
                  name: product.content.productName,
                  quantity: product.quantity,
                  unitPrice: centsToFloat(
                    product.price + product.content.discount
                  ),
                  promotionalPrice: centsToFloat(product.price),
                  fractions:
                    product.content && product.content.fractionated
                      ? product.content.fractionated
                      : [],
                  additionals:
                    product.content && product.content.additionals
                      ? product.content.additionals.map((additional: any) => ({
                          ...additional,
                          price: centsToFloat(additional.price),
                        }))
                      : [],
                  observation:
                    product.content && product.content.observation
                      ? product.content.observation
                      : "",
                  variation: product.content.variation
                    ? JSON.parse(product.content.variation)
                    : {},
                })),
                totalValue: centsToFloat(totalValue),
                freight:
                  order.content && order.content.deliveryFee
                    ? centsToFloat(order.content.deliveryFee)
                    : 0,
                content: order.content,
              };
            }
          );
          setFindedOrders((findedOrders) => [...findedOrders, ...newOrders]);
          if (findedOrders.length > 0 && newOrders.length === 0) {
            Swal.fire({
              icon: "warning",
              title: "Atenção!",
              text: "Todos os pedidos foram carregados.",
            });
            setSearchMore(false);
          }
          setPage((prevPage) => prevPage + 2);
          setLoaderLocation(false);
        });
    } catch (err) {
      setLoaderLocation(false);
      console.log(err);
      return null;
    }
  }

  async function searchOrders() {
    if (numberOrder && !cpfOrCnpj) {
      setLoading(true);
      await api
        .post(
          `/api/orders/ext/${numberOrder}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then((response) => {
          const orderResponse = response.data;
          const newOrders = orderResponse.data.map(
            (order: OrderResponseType) => {
              let orderAddress = null;
              if (order.content && order.content.orderAddress) {
                orderAddress = order.content.orderAddress;
              }
              let totalValue = 0;
              order.products.map((product) => {
                totalValue += product.price * product.quantity;
                return;
              });
              return {
                id: order.id,
                ext: order.orderidExt,
                date: datetimeToDateBR(order.created),
                cartCustomer: order.cartCustomer,
                type: order.type,
                locationName:
                  order.location && order.location.name
                    ? order.location.name
                    : "",
                observation: order.observation,
                statusId: order.statusId,
                status: order.status.sort((a, b) => a.order - b.order),
                allStatus: status,
                store: order.pluginspace
                  ? order.pluginspace.name
                  : orderAddress.name
                  ? orderAddress.name
                  : "",
                address: orderAddress
                  ? `${orderAddress.address} - ${orderAddress.district} - ${
                      orderAddress.city
                    } - ${orderAddress.cep ? orderAddress.cep : ""} ${
                      orderAddress.complement
                        ? " - " + orderAddress.complement
                        : ""
                    }`
                  : "",
                user: {
                  name:
                    order.content && order.content.customer
                      ? order.content.customer
                      : "",
                  cpf:
                    order.content && order.content.customerCpf
                      ? cpfMask(order.content.customerCpf)
                      : "",
                },
                products: order.products.map((product) => ({
                  id: product.id,
                  sku: product.sku,
                  name: product.content.productName,
                  quantity: product.quantity,
                  unitPrice: centsToFloat(
                    product.price + product.content.discount
                  ),
                  promotionalPrice: centsToFloat(product.price),
                  fractions:
                    product.content && product.content.fractionated
                      ? product.content.fractionated
                      : [],
                  additionals:
                    product.content && product.content.additionals
                      ? product.content.additionals.map((additional: any) => ({
                          ...additional,
                          price: centsToFloat(additional.price),
                        }))
                      : [],
                  observation:
                    product.content && product.content.observation
                      ? product.content.observation
                      : "",
                  variation: product.content.variation
                    ? JSON.parse(product.content.variation)
                    : {},
                })),
                totalValue: centsToFloat(totalValue),
                freight:
                  order.content && order.content.deliveryFee
                    ? centsToFloat(order.content.deliveryFee)
                    : 0,
                content: order.content,
              };
            }
          );
          setFindedOrders(newOrders);
          setSearchMore(false);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.log(err);
          return null;
        });
    } else if (!numberOrder && cpfOrCnpj) {
      setLoading(true);
      await api
        .post(
          `/api/orders/filter?take=10&skip=0&page=0&order=DESC`,
          {
            cpf: cpfCnpjNoMask(cpfOrCnpj),
          },
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then((response) => {
          const orderResponse = response.data;

          const newOrders = orderResponse.data.map(
            (order: OrderResponseType) => {
              let orderAddress = null;
              if (order.content && order.content.orderAddress) {
                orderAddress = order.content.orderAddress;
              }
              let totalValue = 0;
              order.products.map((product) => {
                totalValue += product.price * product.quantity;
                return;
              });
              return {
                id: order.id,
                ext: order.orderidExt,
                cartCustomer: order.cartCustomer,
                date: datetimeToDateBR(order.created),
                type: order.type,
                statusId: order.statusId,
                status: order.status.sort((a, b) => a.order - b.order),
                allStatus: status,
                store: order.pluginspace.name,
                locationName:
                  order.location && order.location.name
                    ? order.location.name
                    : "",
                observation: order.observation,
                address: orderAddress
                  ? `${orderAddress.address} - ${orderAddress.district} - ${
                      orderAddress.city
                    } - ${orderAddress.cep ? orderAddress.cep : ""} ${
                      orderAddress.complement
                        ? " - " + orderAddress.complement
                        : ""
                    }`
                  : "",
                user: {
                  name:
                    order.content && order.content.customer
                      ? order.content.customer
                      : "",
                  cpf:
                    order.content && order.content.customerCpf
                      ? cpfMask(order.content.customerCpf)
                      : "",
                },
                products: order.products.map((product) => ({
                  id: product.id,
                  sku: product.sku,
                  name: product.content.productName,
                  quantity: product.quantity,
                  unitPrice: centsToFloat(
                    product.price + product.content.discount
                  ),
                  promotionalPrice: centsToFloat(product.price),
                  fractions:
                    product.content && product.content.fractionated
                      ? product.content.fractionated
                      : [],
                  additionals:
                    product.content && product.content.additionals
                      ? product.content.additionals.map((additional: any) => ({
                          ...additional,
                          price: centsToFloat(additional.price),
                        }))
                      : [],
                  observation:
                    product.content && product.content.observation
                      ? product.content.observation
                      : "",
                  variation: product.content.variation
                    ? JSON.parse(product.content.variation)
                    : {},
                })),
                totalValue: centsToFloat(totalValue),
                freight:
                  order.content && order.content.deliveryFee
                    ? centsToFloat(order.content.deliveryFee)
                    : 0,
                content: order.content,
              };
            }
          );

          setFindedOrders(newOrders);
          setPage(0);
          newOrders.length >= 10 ? setSearchMore(true) : setSearchMore(false);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.log(err);
          return null;
        });
    } else {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: "Informe o documento ou número do pedido",
      });
    }
    setNumberOrder("");
    setDisableDoc(false);
    setDisableNumber(false);
  }

  function changeMaskCpf(newDocument: string) {
    if (newDocument.length >= 14 && newDocument[13] !== "_") {
      setMaskCpfjCnpj("99.999.999/9999-99");
    } else {
      setMaskCpfjCnpj("999.999.999-99");
    }
  }

  return (
    <div className="search-order">
      <div className="content-search-order">
        {loading ? (
          <div style={{ width: "100%", height: "calc(100vh)" }}>
            <Loading
              type="spinningBubbles"
              color="#FFF"
              width={64}
              height={64}
            />
          </div>
        ) : (
          <>
            <div className="title-search">
              <h2>Pesquisa de Pedidos</h2>
              <svg
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => navigate(-1)}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.978551 2.21657L6.957 8.2395L0.978551 14.2624L2.97137 16.2701L8.94981 10.2471L14.9283 16.2701L16.9211 14.2624L10.9426 8.2395L16.9211 2.21657L14.9283 0.208931L8.94981 6.23186L2.97137 0.208931L0.978551 2.21657Z"
                  fill="#636363"
                />
              </svg>
            </div>
            <div className="search-body">
              <p>
                Informe o número do pedido ou CPF informado no momento da compra
                para verificar o status que se encontra o pedido
              </p>
              <div className="search-inputs">
                <input
                  style={disableNumber ? { backgroundColor: "#D5D5D5" } : {}}
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Número do pedido"
                  disabled={disableNumber}
                  value={numberOrder}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") searchOrders();
                  }}
                  onChange={(event) => {
                    setCpfOrCnpj("");
                    setSearchMore(false);
                    const onlyNums = event.target.value.replace(/\D/g, '');
                    setNumberOrder(onlyNums);
                    if (event.target.value !== "") {
                      setDisableDoc(true);
                    } else {
                      setDisableDoc(false);
                    }
                  }}
                />
                <InputMask
                  style={disableDoc ? { backgroundColor: "#D5D5D5" } : {}}
                  mask={maskCpfjCnpj}
                  type="text"
                  value={cpfOrCnpj}
                  disabled={disableDoc}
                  placeholder={`${
                    config?.content.pdv && config?.content.pdv.allowSellCnpj
                      ? "Número do CPF ou CNPJ"
                      : "Número do CPF"
                  }`}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") searchOrders();
                  }}
                  onBeforeInputCapture={(event) =>
                    changeMaskCpf(event.currentTarget.value)
                  }
                  onChange={(event) => {
                    setCpfOrCnpj(event.target.value);
                    if (event.target.value !== "") {
                      setDisableNumber(true);
                    } else {
                      setDisableNumber(false);
                    }
                  }}
                />
              </div>
              <div className="button-search">
                <MainButton
                  color={
                    config?.webappcolor.fontbutton
                      ? config.webappcolor.fontbutton
                      : "#636363"
                  }
                  backgroundColor={
                    config?.webappcolor.webappbutton
                      ? config.webappcolor.webappbutton
                      : "#EAEAEA"
                  }
                  text="Pesquisar"
                  onClick={() => {
                    setSearchMore(true);
                    searchOrders();
                  }}
                />
              </div>
              <div className="orders-finded">
                {findedOrders.map((order) => (
                  <SearchOrderItem
                    order={order}
                    key={order.id}
                    config={config ? config : null}
                    colorStatus={config?.webappcolor.backgroundstatus}
                    colorActiveStatus={
                      config?.webappcolor.backgroundstatusactive
                    }
                    fontStatus={config?.webappcolor.fontstatus}
                  />
                ))}
                {findedOrders.length > 0 && searchMore && (
                  <MainButton
                    color={
                      config?.webappcolor.fontbutton
                        ? config.webappcolor.fontbutton
                        : "#636363"
                    }
                    backgroundColor={
                      config?.webappcolor.webappbutton
                        ? config.webappcolor.webappbutton
                        : "#EAEAEA"
                    }
                    text={loaderLocation ? <></> : "Carregar Mais"}
                    onClick={() => {
                      searchMoreOrders();
                    }}
                    loaderIcon={loaderLocation}
                    disabled={loaderLocation}
                  />
                )}
              </div>
            </div>
          </>
        )}
        {!loadingHeader && (
          <Menu
            activeMenu="order"
            fontColor={
              config?.webappcolor.iconsfooter
                ? config?.webappcolor.iconsfooter
                : "#636363"
            }
            activeFontColor={
              config?.webappcolor.highlightfooter
                ? config?.webappcolor.highlightfooter
                : "#000"
            }
            backgroundMenu={
              config?.webappcolor.backgroundfooter
                ? config?.webappcolor.backgroundfooter
                : "#F5F5F5"
            }
            colorIcons={
              config?.webappcolor.iconsfooter
                ? config?.webappcolor.iconsfooter
                : "#636363"
            }
            highlightColorIcons={
              config?.webappcolor.highlightfooter
                ? config?.webappcolor.highlightfooter
                : "#636363"
            }
            backgroundCart={
              config?.webappcolor.backgroundcart
                ? config.webappcolor.backgroundcart
                : "#FFBA49"
            }
            fontCart={
              config?.webappcolor.fontcart
                ? config.webappcolor.fontcart
                : "#000"
            }
            noSales={
              config &&
              config?.content &&
              config?.content.pdv &&
              config?.content.pdv.noSales
                ? config.content.pdv.noSales
                : false
            }
          />
        )}
      </div>
    </div>
  );
}
